import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, VStack, HStack, Input, Button, Text, IconButton } from '@chakra-ui/react'
import { useState, useEffect, useRef, useMemo } from 'react'
import { subscribeToMatch, getMatch } from '@/services/firebase'
import html2canvas from 'html2canvas'
import { FaEdit } from 'react-icons/fa'
import { useProcessedPointData } from '@/hooks/useProcessedPointData'
import { PointGraph } from '@/components/common/PointGraph'
import { LegendEditor } from '@/components/common/LegendEditor'

interface StatisticsProps {
  isOpen: boolean
  onClose: () => void
  matchId: string
  team1Color: string
  team2Color: string
  team1Name?: string
  team2Name?: string
  setScores?: Array<{ team1: number; team2: number; tiebreakScore?: { team1: number; team2: number } }>
  currentSetScore?: { team1: number; team2: number }
}

export function Statistics({ 
  isOpen, 
  onClose, 
  matchId, 
  team1Color, 
  team2Color, 
  team1Name, 
  team2Name,
  setScores = [],
  currentSetScore
}: StatisticsProps) {
  const [data, setData] = useState<any>(null)
  const [isEditingTeam1, setIsEditingTeam1] = useState(false)
  const [isEditingTeam2, setIsEditingTeam2] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)
  const captureRef = useRef<HTMLDivElement>(null)
  const bgColor = '#09090b'
  const textColor = 'white'
  const borderColor = '#444'

  // Fallback: Fetch set scores directly from Firebase if props don't have tie-break info
  const [firebaseSetScores, setFirebaseSetScores] = useState<Array<{ team1: number; team2: number; tiebreakScore?: { team1: number; team2: number } }>>([]);
  const [currentTiebreakPoints, setCurrentTiebreakPoints] = useState<{ team1: number; team2: number } | null>(null);
  const [isTiebreakActive, setIsTiebreakActive] = useState(false);
  
  useEffect(() => {
    if (isOpen && matchId) {
      const fetchSetScores = async () => {
        try {
          const matchData = await getMatch(matchId);
          if (matchData?.setScores) {
            setFirebaseSetScores(matchData.setScores);
          }
          // Also fetch current tie-break information
          if (matchData?.isTiebreak && matchData?.tiebreakPoints) {
            setCurrentTiebreakPoints(matchData.tiebreakPoints);
            setIsTiebreakActive(true);
          } else {
            setCurrentTiebreakPoints(null);
            setIsTiebreakActive(false);
          }
        } catch (error) {
          console.error('Error fetching set scores from Firebase:', error);
        }
      };
      fetchSetScores();
    }
  }, [isOpen, matchId]);

  // Use Firebase data if it has tie-break info, otherwise use props
  const displaySetScores = firebaseSetScores.some(score => score.tiebreakScore) ? firebaseSetScores : setScores;

  // Identify Super Tie-break sets (sets with tie-break scores >= 10)
  const superTiebreakSets = displaySetScores
    .map((setScore, index) => setScore.tiebreakScore && 
      (setScore.tiebreakScore.team1 >= 10 || setScore.tiebreakScore.team2 >= 10) ? index : -1)
    .filter(index => index !== -1);

  // Helper function to determine team display names
  const getTeamDisplayNames = (team1Color: string, team2Color: string, team1Name?: string, team2Name?: string) => {
    const hasTeam1Name = team1Name && team1Name.trim() !== ''
    const hasTeam2Name = team2Name && team2Name.trim() !== ''
    
    if (!hasTeam1Name && !hasTeam2Name) {
      // Both names empty - use color names
      return {
        team1: `${team1Color.charAt(0).toUpperCase() + team1Color.slice(1)}`,
        team2: `${team2Color.charAt(0).toUpperCase() + team2Color.slice(1)}`
      }
    } else if (hasTeam1Name && !hasTeam2Name) {
      // Only team1 has a name - use name + "Opponent"
      return {
        team1: team1Name,
        team2: 'Opponent'
      }
    } else if (!hasTeam1Name && hasTeam2Name) {
      // Only team2 has a name - use "Opponent" + name
      return {
        team1: 'Opponent',
        team2: team2Name
      }
    } else {
      // Both names provided - use both names
      return {
        team1: team1Name,
        team2: team2Name
      }
    }
  }

  const [legendText, setLegendText] = useState(() => 
    getTeamDisplayNames(team1Color, team2Color, team1Name, team2Name)
  )

  // Use the hook to process point data
  const pointData = useProcessedPointData(data?.events, team1Color, team2Color)

  // Calculate total points for each team
  const totalPoints = useMemo(() => {
    if (!pointData.length) return { team1: 0, team2: 0 }
    
    let team1Points = 0
    let team2Points = 0
    
    pointData.forEach(point => {
      if (point.color === team1Color) {
        team1Points++
      } else {
        team2Points++
      }
    })
    
    return { team1: team1Points, team2: team2Points }
  }, [pointData, team1Color, team2Color])

  useEffect(() => {
    if (!isOpen) return

    const unsubscribe = subscribeToMatch(matchId, (data: any) => {
      setData(data)
    })

    return () => {
      unsubscribe()
    }
  }, [isOpen, matchId])

  const handleTeam1NameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLegendText(prev => ({ ...prev, team1: e.target.value }));
  };

  const handleTeam2NameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLegendText(prev => ({ ...prev, team2: e.target.value }));
  };

  // Update legend text when props change
  useEffect(() => {
    setLegendText(getTeamDisplayNames(team1Color, team2Color, team1Name, team2Name));
  }, [team1Name, team2Name, team1Color, team2Color]);

  const handleSaveGraph = async () => {
    if (!captureRef.current) return;

    // Wait for the next frame to ensure everything is rendered
    await new Promise(resolve => requestAnimationFrame(resolve));

    try {
      // Create a canvas with the same dimensions as the capture container
      const canvas = await html2canvas(captureRef.current, {
        scale: 2, // Higher scale for better quality
        backgroundColor: '#09090b', // Match the dark theme background
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: Math.max(600, captureRef.current.offsetWidth), // Ensure minimum width of 600px
        onclone: (clonedDoc) => {
          // Ensure all text elements are visible
          const textElements = clonedDoc.getElementsByTagName('text');
          Array.from(textElements).forEach(text => {
            text.setAttribute('fill', 'white');
          });

          // Ensure SVG elements are visible
          const svgElements = clonedDoc.getElementsByTagName('svg');
          Array.from(svgElements).forEach(svg => {
            svg.style.display = 'block';
            svg.style.visibility = 'visible';
            // Ensure SVG maintains minimum width
            svg.style.minWidth = '600px';
          });

          // Ensure the legend is visible
          const legendElements = clonedDoc.querySelectorAll('.recharts-legend-wrapper');
          Array.from(legendElements).forEach(legend => {
            (legend as HTMLElement).style.display = 'block';
            (legend as HTMLElement).style.visibility = 'visible';
          });
        }
      });

      // Create download link
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `${legendText.team1}-${legendText.team2}[${timestamp}].png`;
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error saving graph:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent 
        bg={bgColor} 
        maxW="90vw" 
        maxH="90vh" 
        my="5vh"
        overflow="hidden"
        position="relative"
        display="flex"
        flexDirection="column"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: bgColor,
          zIndex: -1
        }}
      >
        <ModalHeader>
          <VStack spacing={0} align="start">
            <Text color="white">Match Statistics</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton color={textColor} />
        <ModalBody pb={6} position="relative" overflowY="auto" flex="1">
          <VStack spacing={4} align="stretch" minH="min-content">
            <Box ref={captureRef} bg={bgColor} minW="600px" overflowX="auto">
              <Text fontSize="sm" color="gray.400" textAlign="center" width="100%" mb={4}>AiScoringApp.com</Text>
              <HStack spacing={4} mb={4} justify="center" minW="600px">
                {/* Team 1 Name */}
                <HStack spacing={2} align="center">
                  <LegendEditor
                    name={legendText.team1 ?? ''}
                    color={team1Color}
                    isEditing={isEditingTeam1}
                    onStartEdit={() => setIsEditingTeam1(true)}
                    onChange={(val) =>
                      setLegendText(prev => ({ ...prev, team1: val }))
                    }
                    onSave={() => setIsEditingTeam1(false)}
                  />
                </HStack>

                {/* Team 2 Name */}
                <HStack spacing={2} align="center">
                  <LegendEditor
                    name={legendText.team2 ?? ''}
                    color={team2Color}
                    isEditing={isEditingTeam2}
                    onStartEdit={() => setIsEditingTeam2(true)}
                    onChange={(val) =>
                      setLegendText(prev => ({ ...prev, team2: val }))
                    }
                    onSave={() => setIsEditingTeam2(false)}
                  />
                </HStack>
              </HStack>

              {/* Total Points Display */}
              <HStack spacing={4} mb={4} justify="center" minW="600px">
                <Text fontSize="sm" color="gray.400">
                  Total Points:{' '}
                  <Text as="span" color={`${team1Color}.500`} fontWeight="bold">
                    {totalPoints.team1}
                  </Text>
                  {'-'}
                  <Text as="span" color={`${team2Color}.500`} fontWeight="bold">
                    {totalPoints.team2}
                  </Text>
                </Text>
              </HStack>

              {/* Set Scores Display */}
              {(displaySetScores.length > 0 || currentSetScore) && (
                <VStack spacing={2} mb={4} align="center">
                  <HStack spacing={4} justify="center" flexWrap="wrap">
                    {displaySetScores.map((setScore, index) => (
                      <Text 
                        key={index}
                        fontSize="sm" 
                        color="gray.400"
                        whiteSpace="nowrap"
                      >
                        Set {index + 1}:{' '}
                        <Text as="span" color={`${team1Color}.500`}>{setScore.team1}</Text>
                        {'-'}
                        <Text as="span" color={`${team2Color}.500`}>{setScore.team2}</Text>
                        {setScore.tiebreakScore && (
                          <Text as="span" color="gray.500" fontSize="xs">
                            {' '}({setScore.tiebreakScore.team1}-{setScore.tiebreakScore.team2})
                          </Text>
                        )}
                      </Text>
                    ))}
                    {currentSetScore && (
                      <Text 
                        fontSize="sm" 
                        color="gray.400"
                        whiteSpace="nowrap"
                      >
                        Set {displaySetScores.length + 1}:{' '}
                        <Text as="span" color={`${team1Color}.500`}>
                          {currentSetScore.team1}
                        </Text>
                        {'-'}
                        <Text as="span" color={`${team2Color}.500`}>
                          {currentSetScore.team2}
                        </Text>
                        {isTiebreakActive && currentTiebreakPoints && (
                          <Text as="span" color="gray.500" fontSize="xs">
                            {' '}({currentTiebreakPoints.team1}-{currentTiebreakPoints.team2})
                          </Text>
                        )}
                      </Text>
                    )}
                  </HStack>
                </VStack>
              )}

              <Box width="100%" height="400px" ref={chartRef} className="chart-container">
                <PointGraph 
                  data={pointData} 
                  team1Color={team1Color} 
                  team2Color={team2Color} 
                  superTiebreakSets={superTiebreakSets}
                />
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
              <Text color="gray.500" mb={2} textAlign="center" width="100%">
                To keep these stats:
              </Text>
              <Button
                onClick={handleSaveGraph}
                colorScheme="blue"
                variant="outline"
                size="lg"
                width="200px"
                color="#3182ce"
                borderColor="#3182ce"
                _hover={{
                  bg: 'rgba(49, 130, 206, 0.1)',
                  borderColor: '#3182ce'
                }}
              >
                Save Graph
              </Button>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
} 